import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  File,
  FileArchive,
  FileAudio,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Loader2,
  Paperclip,
  Trash2,
  X,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { attachmentApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import {
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENTS_PER_TASK,
  describeUploadFailure,
  fileKind,
  formatBytes,
  DISARM_MS,
} from "./attachmentFormat";
import PhotoViewer from "./PhotoViewer";
import UserAvatar from "../people/UserAvatar";
import { displayName, relativeTime } from "./userDisplay";

const KIND_ICON = {
  image: FileImage,
  video: FileVideo,
  audio: FileAudio,
  document: FileText,
  text: FileText,
  sheet: FileSpreadsheet,
  archive: FileArchive,
  file: File,
};

// Each listing's download links live ~10 minutes; re-list a little
// before that so a dialog left open never offers a dead link. Also
// re-list on return to the tab if the last good listing is older.
const RELIST_MS = 8 * 60 * 1000;
// A signed upload URL lives 15 minutes; past ~14 a retry starts over.
const UPLOAD_URL_REUSE_MS = 14 * 60 * 1000;
// An armed "Remove for everyone?" disarms itself (house rule).

let uploadSeq = 0;

const hasFiles = (event) =>
  Array.from(event.dataTransfer?.types || []).includes("Files");

/**
 * The task's files: a drop zone + "Attach files" button, uploads with
 * progress, and the list (thumbnails for photos, download links, remove).
 *
 * An upload is three steps — `start` (a pending row + signed URL), the PUT
 * straight to storage, `complete` — and each entry remembers how far it
 * got, so Retry resumes from the step that failed (a lost `complete`
 * response never sends a 1 GB file twice). Cancelled and abandoned
 * uploads discard their pending row so it doesn't hold a slot under the
 * per-task cap. While anything is in flight the dialog can't be saved or
 * closed (`onBusyChange`); the user cancels uploads explicitly instead.
 *
 * Renders inside TaskModal's <form>: every button here is type="button".
 */
function TaskAttachments({
  taskId,
  me = null,
  canManage = false,
  onChange = undefined,
  onBusyChange = undefined,
  refreshKey = 0,
}) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [removeError, setRemoveError] = useState(null);
  const [announcement, setAnnouncement] = useState("");
  // The photo open in the viewer, by id (survives re-lists and removals).
  const [viewingId, setViewingId] = useState(null);
  const inputRef = useRef(null);
  const sectionRef = useRef(null);
  const controllers = useRef(new Map());
  const uploadsRef = useRef(uploads);
  uploadsRef.current = uploads;
  // Bumped by every local change to the list; a listing requested before
  // the latest change is stale and dropped (it would erase a just-finished
  // upload or resurrect a just-removed file).
  const generation = useRef(0);
  const lastListedAt = useRef(0);
  const disarmTimer = useRef(null);
  // Mirrors viewingId synchronously (set together, via showPhoto): an
  // awaited removal must see where the viewer is NOW, not at its click.
  const viewingRef = useRef(null);

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      const requestedAt = generation.current;
      if (!quiet) setLoading(true);
      setLoadError(null);
      try {
        const { data } = await attachmentApi.list(taskId);
        if (requestedAt !== generation.current) return;
        setFiles(data || []);
        lastListedAt.current = Date.now();
      } catch (err) {
        if (!quiet) setLoadError(err);
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [taskId],
  );

  useEffect(() => {
    load();
    const timer = setInterval(() => load({ quiet: true }), RELIST_MS);
    const onReturn = () => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - lastListedAt.current > RELIST_MS
      ) {
        load({ quiet: true });
      }
    };
    document.addEventListener("visibilitychange", onReturn);
    window.addEventListener("focus", onReturn);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onReturn);
      window.removeEventListener("focus", onReturn);
    };
  }, [load]);

  // Someone else attached or removed a file (the live thread saw it —
  // projects/task-live-refresh D8): re-list quietly, not on mount.
  useEffect(() => {
    if (refreshKey) load({ quiet: true });
  }, [refreshKey, load]);

  // A file dropped anywhere else in the dialog would make the browser open
  // it — navigating away from the board mid-upload. Swallow those.
  useEffect(() => {
    const swallow = (e) => {
      if (hasFiles(e)) e.preventDefault();
    };
    document.addEventListener("dragover", swallow);
    document.addEventListener("drop", swallow);
    return () => {
      document.removeEventListener("dragover", swallow);
      document.removeEventListener("drop", swallow);
    };
  }, []);

  const discardPending = (id) => {
    // Best effort: a pending row holds a slot under the per-task cap until
    // the server's day-old purge. The uploader may delete their own.
    attachmentApi.delete(id)?.catch?.(() => {});
  };

  // Unmount: stop in-flight transfers (their catch discards the row) and
  // discard rows of failed uploads nobody will retry now.
  useEffect(() => {
    const live = controllers.current;
    return () => {
      live.forEach((c) => c.abort());
      uploadsRef.current
        .filter(
          (u) =>
            u.status === "error" && u.started && u.failedStep !== "complete",
        )
        .forEach((u) => discardPending(u.started.id));
      clearTimeout(disarmTimer.current);
    };
  }, []);

  const busy = uploads.some(
    (u) => u.status === "uploading" || u.status === "finishing",
  );
  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  const patchUpload = (key, patch) =>
    setUploads((prev) =>
      prev.map((u) => (u.key === key ? { ...u, ...patch } : u)),
    );
  const dropUpload = (key) => {
    controllers.current.delete(key);
    setUploads((prev) => prev.filter((u) => u.key !== key));
  };

  const runUpload = async (entry) => {
    const { key, file } = entry;
    let started = entry.started || null;
    let step = entry.failedStep || "start";
    if (
      step === "upload" &&
      (!started || Date.now() - started.at > UPLOAD_URL_REUSE_MS)
    ) {
      if (started) discardPending(started.id);
      started = null;
    }
    if (step !== "complete" && !started) step = "start";

    const controller = new AbortController();
    controllers.current.set(key, controller);
    patchUpload(key, {
      status: step === "complete" ? "finishing" : "uploading",
      progress: step === "complete" ? 1 : 0,
      error: null,
      started,
    });
    try {
      if (step === "start") {
        const { data } = await attachmentApi.start(taskId, {
          name: file.name,
          size: file.size,
          content_type: file.type || "",
        });
        started = {
          id: data.id,
          url: data.upload_url,
          headers: data.upload_headers,
          at: Date.now(),
        };
        patchUpload(key, { started });
        if (controller.signal.aborted) throw new Error("aborted");
        step = "upload";
      }
      if (step === "upload") {
        await attachmentApi.upload(started.url, file, started.headers, {
          signal: controller.signal,
          onProgress: (fraction) => patchUpload(key, { progress: fraction }),
        });
        step = "complete";
        // Past this point there is nothing to cancel: the bytes are in
        // storage and `complete` only publishes them.
        controllers.current.delete(key);
        patchUpload(key, { status: "finishing", progress: 1 });
      }
      const { data } = await attachmentApi.complete(started.id);
      generation.current += 1;
      setFiles((prev) =>
        [...prev.filter((f) => f.id !== data.id), data].sort((a, b) =>
          a.created_at.localeCompare(b.created_at),
        ),
      );
      dropUpload(key);
      setAnnouncement(`${file.name} attached.`);
      onChange?.();
    } catch (err) {
      if (controller.signal.aborted && step !== "complete") {
        if (started) discardPending(started.id);
        dropUpload(key);
        setAnnouncement(`${file.name} cancelled.`);
        return;
      }
      controllers.current.delete(key);
      const failure = describeUploadFailure(step, err, apiErrorMessage);
      if (failure.resumeFrom === "start" && started && failure.rowSurvives) {
        discardPending(started.id);
      }
      patchUpload(key, {
        status: "error",
        error: failure.message,
        retryable: failure.retryable,
        failedStep: failure.resumeFrom,
        started: failure.resumeFrom === "start" ? null : started,
      });
    }
  };

  const dismissUpload = (u) => {
    // After a failed `complete` the file may in fact be attached (only the
    // response was lost): never delete it from here; a relist tells.
    if (u.started && u.failedStep !== "complete") discardPending(u.started.id);
    if (u.failedStep === "complete") load({ quiet: true });
    dropUpload(u.key);
  };

  const full = files.length >= MAX_ATTACHMENTS_PER_TASK;

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    if (incoming.length === 0 || full) return;
    const added = incoming.map((file) => {
      uploadSeq += 1;
      const base = {
        key: `upload-${uploadSeq}`,
        file,
        progress: 0,
        started: null,
      };
      if (file.size > MAX_ATTACHMENT_BYTES) {
        return {
          ...base,
          status: "error",
          retryable: false,
          error: `${file.name} is over the 1 GB limit per file (${formatBytes(file.size)}).`,
        };
      }
      if (file.size === 0) {
        return {
          ...base,
          status: "error",
          retryable: false,
          error: `${file.name} is empty (or a folder) — only files can be attached.`,
        };
      }
      return { ...base, status: "uploading", error: null, retryable: true };
    });
    setUploads((prev) => [...prev, ...added]);
    added.filter((u) => u.status === "uploading").forEach((u) => runUpload(u));
  };

  const focusIn = (selector) =>
    requestAnimationFrame(() =>
      sectionRef.current?.querySelector(selector)?.focus(),
    );

  const arm = (id) => {
    setRemoveError(null);
    setConfirmId(id);
    clearTimeout(disarmTimer.current);
    disarmTimer.current = setTimeout(() => setConfirmId(null), DISARM_MS);
    focusIn(`[data-keep="${id}"]`);
  };
  const disarm = (id) => {
    clearTimeout(disarmTimer.current);
    setConfirmId(null);
    focusIn(`[data-remove="${id}"]`);
  };

  const handleRemove = async (f) => {
    clearTimeout(disarmTimer.current);
    setRemovingId(f.id);
    setRemoveError(null);
    try {
      await attachmentApi.delete(f.id);
      generation.current += 1;
      setFiles((prev) => prev.filter((x) => x.id !== f.id));
      setConfirmId(null);
      setAnnouncement(`${f.name} removed.`);
      onChange?.();
      return true;
    } catch (err) {
      setRemoveError(apiErrorMessage(err, "Couldn't remove the file."));
      return false;
    } finally {
      setRemovingId(null);
    }
  };

  const mayRemove = (f) => me == null || canManage || f.uploaded_by?.id === me;

  // Photos (anything with a thumbnail) show as a grid of tiles and open in
  // the viewer; everything else — including an image too big to shrink —
  // stays in the list with its type icon. The thumbnail URL is stable all
  // day, so re-opening the dialog costs nothing (photos-and-avatars).
  const photos = files.filter((f) => f.thumbnail_url);
  const others = files.filter((f) => !f.thumbnail_url);
  const viewingIndex = photos.findIndex((f) => f.id === viewingId);

  const showPhoto = (id) => {
    viewingRef.current = id;
    setViewingId(id);
  };
  const removeFromViewer = async (f) => {
    const i = photos.findIndex((x) => x.id === f.id);
    const next = photos[i + 1] ?? photos[i - 1] ?? null;
    // Move on only if the viewer is still on the removed photo — stepping
    // away while the delete was in flight must not be pulled back.
    if ((await handleRemove(f)) && viewingRef.current === f.id)
      showPhoto(next?.id ?? null);
  };
  // Closing the viewer hands focus back to the tile of the photo it was
  // showing (the one it opened from may be gone), else the grid's first
  // tile, else the section — never <body>.
  const focusAfterViewer = () => {
    const section = sectionRef.current;
    const target =
      section?.querySelector(`[data-tile="${viewingRef.current}"]`) ??
      section?.querySelector("[data-tile]") ??
      section?.querySelector("h3");
    target?.focus();
  };

  return (
    <section
      ref={sectionRef}
      aria-label="Attachments"
      className="mt-4 border-t pt-4"
    >
      <h3
        tabIndex={-1}
        className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase outline-none"
      >
        Attachments{files.length > 0 && ` (${files.length})`}
      </h3>
      <p className="sr-only" aria-live="polite">
        {announcement}
      </p>

      <div
        data-testid="attachment-dropzone"
        className={cn(
          "rounded-md border border-dashed px-3 py-3 text-sm transition-colors",
          dragging && !full ? "border-primary bg-primary/5" : "border-border",
        )}
        onDragOver={(e) => {
          if (!hasFiles(e) || full) return;
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          addFiles(e.dataTransfer?.files);
        }}
      >
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={full}
            onClick={() => inputRef.current?.click()}
          >
            <Paperclip className="size-4" />
            Attach files
          </Button>
          <span className="text-muted-foreground">
            {full
              ? `This task has the maximum of ${MAX_ATTACHMENTS_PER_TASK} files — remove one to add another.`
              : "or drop them here"}
          </span>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Up to 1 GB each, any type. Everyone who can see this task can open
          them. Files are kept exactly as uploaded — photos keep their location
          data.
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="sr-only"
          tabIndex={-1}
          aria-label="Choose files to attach"
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {uploads.length > 0 && (
        <ul className="mt-2 list-none space-y-2 p-0" aria-label="Uploads">
          {uploads.map((u) => (
            <li key={u.key} className="rounded-md border px-3 py-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate">{u.file.name}</span>
                <span className="text-xs text-muted-foreground">
                  {u.status === "uploading" &&
                    `Uploading ${Math.round(u.progress * 100)}% of ${formatBytes(u.file.size)}`}
                  {u.status === "finishing" && "Finishing…"}
                </span>
                {u.status === "error" && u.retryable && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => runUpload(u)}
                  >
                    Retry
                  </Button>
                )}
                {u.status === "uploading" && (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="size-7"
                    aria-label={`Cancel ${u.file.name}`}
                    onClick={() => controllers.current.get(u.key)?.abort()}
                  >
                    <X className="size-4" />
                  </Button>
                )}
                {u.status === "error" && (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="size-7"
                    aria-label={`Dismiss ${u.file.name}`}
                    onClick={() => dismissUpload(u)}
                  >
                    <X className="size-4" />
                  </Button>
                )}
              </div>
              {u.status !== "error" && (
                <div
                  className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted"
                  role="progressbar"
                  aria-label={`Uploading ${u.file.name}`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(u.progress * 100)}
                >
                  <div
                    className="h-full bg-primary transition-[width]"
                    style={{ width: `${Math.round(u.progress * 100)}%` }}
                  />
                </div>
              )}
              {u.status === "error" && (
                <p className="mt-1 text-destructive" role="alert">
                  {u.error}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
      {busy && (
        <p className="mt-2 text-xs text-muted-foreground">
          Save and close wait until uploads finish — cancel an upload to stop
          it.
        </p>
      )}

      {loading && (
        <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading files…
        </div>
      )}

      {!loading && loadError && (
        <Alert className="mt-2 py-2">
          <AlertDescription className="flex items-center gap-3">
            <span className="flex-1 text-sm">
              Couldn&apos;t load the files.
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => load()}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {removeError && (
        <Alert variant="destructive" className="mt-2 py-2">
          <AlertDescription className="text-sm">{removeError}</AlertDescription>
        </Alert>
      )}

      {!loading && !loadError && photos.length > 0 && (
        <ul
          className="mt-2 grid list-none grid-cols-3 gap-2 p-0 sm:grid-cols-4"
          aria-label="Photos"
        >
          {photos.map((f, i) => (
            <li key={f.id}>
              <button
                type="button"
                className="group block aspect-square w-full overflow-hidden rounded-md border bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                data-tile={f.id}
                aria-label={`View ${f.name}, photo ${i + 1} of ${photos.length}`}
                title={f.name}
                onClick={() => showPhoto(f.id)}
              >
                <img
                  src={f.thumbnail_url}
                  alt=""
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  className="size-full object-cover transition-transform group-hover:scale-105"
                />
              </button>
            </li>
          ))}
        </ul>
      )}

      {viewingIndex >= 0 && (
        <PhotoViewer
          photos={photos}
          index={viewingIndex}
          onIndexChange={(i) => showPhoto(photos[i].id)}
          onClose={() => setViewingId(null)}
          onReturnFocus={focusAfterViewer}
          mayRemove={mayRemove}
          onRemove={removeFromViewer}
          removing={removingId === viewingId}
          removeError={removeError}
        />
      )}

      {!loading && !loadError && others.length > 0 && (
        <ul className="mt-2 list-none space-y-1 p-0" aria-label="Files">
          {others.map((f) => {
            const Icon = KIND_ICON[fileKind(f.name, f.content_type)];
            return (
              <li
                key={f.id}
                className="flex items-center gap-3 rounded-md px-1 py-1 text-sm"
              >
                <span className="flex size-10 shrink-0 items-center justify-center rounded border bg-muted">
                  <Icon
                    className="size-5 text-muted-foreground"
                    aria-hidden="true"
                  />
                </span>
                <div className="min-w-0 flex-1">
                  <a
                    href={f.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block truncate font-medium text-foreground underline-offset-2 hover:underline"
                    title={
                      f.previewable ? `Open ${f.name}` : `Download ${f.name}`
                    }
                  >
                    {f.name}
                  </a>
                  <div className="text-xs text-muted-foreground">
                    {formatBytes(f.size)}
                    {f.uploaded_by && (
                      <>
                        {" · "}
                        <UserAvatar
                          user={f.uploaded_by}
                          size="xs"
                          decorative
                          className="mr-1 size-4 align-text-bottom text-[8px]"
                        />
                        {displayName(f.uploaded_by)}
                      </>
                    )}
                    {" · "}
                    <span title={f.created_at}>
                      {relativeTime(f.created_at)}
                    </span>
                  </div>
                </div>
                {mayRemove(f) &&
                  (confirmId === f.id ? (
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-muted-foreground">
                        Remove for everyone?
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        disabled={removingId === f.id}
                        onClick={() => handleRemove(f)}
                      >
                        {removingId === f.id ? "Removing…" : "Remove"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        data-keep={f.id}
                        disabled={removingId === f.id}
                        onClick={() => disarm(f.id)}
                      >
                        Keep
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-8"
                      data-remove={f.id}
                      aria-label={`Remove ${f.name}`}
                      onClick={() => arm(f.id)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  ))}
              </li>
            );
          })}
        </ul>
      )}

      {!loading && !loadError && files.length === 0 && uploads.length === 0 && (
        <p className="mt-2 text-sm text-muted-foreground italic">
          No files yet — screenshots, PDFs, logs, anything up to 1 GB.
        </p>
      )}
    </section>
  );
}

TaskAttachments.propTypes = {
  taskId: PropTypes.number.isRequired,
  me: PropTypes.number,
  canManage: PropTypes.bool,
  onChange: PropTypes.func,
  onBusyChange: PropTypes.func,
  // bumped when someone else changes this task's files
  refreshKey: PropTypes.number,
};

export default TaskAttachments;
